import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '@/lib/api';
import Breadcrumb from '@/components/Breadcrumb';
import { Stethoscope, Check, AlertTriangle, ArrowRight, Sparkles, UserPlus, FileText, CheckCircle, HelpCircle, Calendar } from 'lucide-react';
import { getSpecialtyCopy, SPECIALIZATIONS, LEGACY_SPECIALTY_REDIRECTS } from '@/data/platformSeoData';
import { TOP_CITIES, buildSpecialtyFaqs, buildSpecialtyIntro, DOCSITE_AI_TEAM } from '@/data/specialtyEnrichment';
import JumpToNav from '@/components/JumpToNav';

const DEFAULT_PRICING = [
  { name: 'Gold', price: 'Free', subtitle: 'First 200 doctors · Rs. 99/month thereafter — website + bookings + WhatsApp CTA' },
  { name: 'Coffee Premium', price: 'Rs. 299/mo', subtitle: '+ AI procedures + case studies + data export' },
  { name: 'TurboCharge', price: 'Rs. 999/mo', subtitle: '+ AI SEO engine + lead tracking + Google rich results' },
];

export default function ForSpecialtyPage() {
  const { specialty } = useParams();
  const data = getSpecialtyCopy(specialty);
  const [liveProcedures, setLiveProcedures] = useState([]);
  const [doctorCount, setDoctorCount] = useState(null);

  // Non-blocking: fetch live per-doctor procedure pages for this specialty
  useEffect(() => {
    if (!data) return;
    api.get(`/platform-seo/live-procedures/${specialty}`)
      .then(r => setLiveProcedures(r.data?.procedures || []))
      .catch(() => setLiveProcedures([]));
    // Live signal: how many specialists already on DocSite for this specialty
    api.get(`/platform-seo/for-specialty/${specialty}`)
      .then(r => setDoctorCount(r.data?.doctor_count ?? null))
      .catch(() => setDoctorCount(null));
  }, [specialty, data]);

  // Legacy slug? Redirect to canonical (e.g., /for/general_medicine → /for/physician).
  // Done AFTER hooks to respect Rules of Hooks.
  const redirectTo = LEGACY_SPECIALTY_REDIRECTS[specialty];
  if (redirectTo) {
    return <Navigate to={`/for/${redirectTo}`} replace />;
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="for-specialty-error">
        Specialty not found. <Link to="/" className="text-sky-600 hover:underline ml-2">Back home</Link>
      </div>
    );
  }

  const canonical = `https://docsite.care/for/${specialty}`;
  const title = `${data.specialty_name} Specialists, Direct to Patients | DocSite`;
  const description = `DocSite is the platform that brings the doctor–patient conversation back to ${data.specialty_name.toLowerCase()} specialists. Build your DocSite practice surface in 10 minutes — direct bookings, AI procedure pages, Google-friendly SEO. Free tier available. Specialist-led care, not hospital marketing.`;
  const intro = buildSpecialtyIntro(data.specialty_name, specialty);
  const faqs = buildSpecialtyFaqs(data.specialty_name);
  const lastReviewed = new Date().toISOString().slice(0, 10);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: title,
      description,
      url: canonical,
      datePublished: '2026-01-01',
      dateModified: lastReviewed,
      author: {
        '@type': 'Organization',
        name: DOCSITE_AI_TEAM.name,
        url: DOCSITE_AI_TEAM.url,
        description: DOCSITE_AI_TEAM.description,
      },
      publisher: {
        '@type': 'Organization',
        name: 'DocSite',
        url: 'https://docsite.care',
        logo: { '@type': 'ImageObject', url: 'https://docsite.care/logo512.png' },
      },
      about: { '@type': 'MedicalSpecialty', name: data.specialty_name },
      mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(f => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  // Top 6 related specialties + 6 cities for internal-link cross-pollination
  const relatedSpecialties = SPECIALIZATIONS.filter(s => s.slug !== specialty).slice(0, 6);
  const relatedCities = TOP_CITIES.slice(0, 6);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={canonical} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <section className="bg-gradient-to-br from-sky-50 via-white to-emerald-50 border-b border-slate-100">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28 py-10 md:py-16">
          <Breadcrumb items={[
            { label: 'DocSite', to: '/' },
            { label: 'For Doctors', to: '/for' },
            { label: data.specialty_name },
          ]} />

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            {/* Left column — narrative + CTAs */}
            <div className="lg:col-span-7 xl:col-span-7">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-sky-600 mb-3 flex items-center gap-1.5">
                <Stethoscope size={12} /> Built for {data.specialty_name}
              </p>
              <h1 className="font-medium text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(2.25rem, 3.6vw, 4.25rem)', lineHeight: 1.1 }} data-testid="for-specialty-h1">
                {data.hook}
              </h1>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-4 text-xs text-slate-500" data-testid="for-specialty-byline">
                <span className="inline-flex items-center gap-1.5"><Sparkles size={11} className="text-sky-500" /> By <strong className="text-slate-700">DocSite AI Team</strong></span>
                <span className="text-slate-300">·</span>
                <span className="inline-flex items-center gap-1.5"><Calendar size={11} /> Last reviewed {lastReviewed}</span>
              </div>
              <p className="text-slate-600 mt-6 leading-relaxed" style={{ fontSize: 'clamp(1.05rem, 1.15vw, 1.3rem)' }}>{intro}</p>
              <p className="text-slate-600 mt-4 leading-relaxed" style={{ fontSize: 'clamp(1rem, 1.05vw, 1.2rem)' }}>
                DocSite is the platform that brings the doctor–patient conversation back to {data.specialty_name.toLowerCase()} specialists — own your brand, own your patient relationships, own your Google visibility. Not a website builder. Not a directory. A product platform built so the specialist, not a hospital marketing team or an aggregator algorithm, is the patient's first call.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/register?as=doctor&from=specialty" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-slate-900 text-white font-semibold hover:bg-sky-600 transition-colors" style={{ fontSize: 'clamp(0.95rem, 1.05vw, 1.1rem)' }} data-testid="for-specialty-cta-primary">
                  Start Free <ArrowRight size={16} />
                </Link>
                <Link to="/brochure" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 border-slate-300 text-slate-700 font-semibold hover:border-sky-400 hover:text-sky-700 transition-colors" style={{ fontSize: 'clamp(0.95rem, 1.05vw, 1.1rem)' }}>
                  See pricing
                </Link>
              </div>

              <div className="mt-8 pt-6 border-t border-slate-200 space-y-4">
                <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-slate-600 font-medium" style={{ fontSize: 'clamp(0.95rem, 1.05vw, 1.2rem)' }} data-testid="for-specialty-trust-row">
                  <span className="flex items-center gap-2"><CheckCircle size={20} className="text-emerald-500" /> Free to start</span>
                  <span className="flex items-center gap-2"><CheckCircle size={20} className="text-emerald-500" /> Specialty-tuned content</span>
                  <span className="flex items-center gap-2"><CheckCircle size={20} className="text-emerald-500" /> No middlemen</span>
                </div>

                <JumpToNav
                  testId="for-specialty-jump-nav"
                  className="!mt-0 !pt-0 !border-0"
                  items={[
                    { icon: AlertTriangle, label: 'Why others fail', href: '#pain-points', color: 'rose' },
                    { icon: Sparkles, label: 'What you get', href: '#wins', color: 'emerald' },
                    { icon: Stethoscope, label: 'Specialist first', href: '#specialist-first', color: 'sky' },
                    { icon: FileText, label: 'See pricing', to: '/brochure', color: 'slate' },
                    { icon: UserPlus, label: 'Start FREE', to: '/register?as=doctor&from=specialty_jumpnav', color: 'violet' },
                  ]}
                />
              </div>
            </div>

            {/* Right column — live signal + sample preview */}
            <aside className="lg:col-span-5 xl:col-span-5 lg:sticky lg:top-24" data-testid="for-specialty-rail">
              {/* Live signal card */}
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                  </span>
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-700">Live on DocSite</p>
                </div>
                <p className="text-4xl font-medium text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {doctorCount === null ? '—' : doctorCount}
                  <span className="text-base font-normal text-slate-500 ml-2">{(doctorCount ?? 0) === 1 ? `${data.specialty_name.toLowerCase()} specialist` : `${data.specialty_name.toLowerCase()} specialists`}</span>
                </p>
                <p className="text-xs text-slate-500 mt-1">already publishing on DocSite. The next one could be you.</p>
              </div>

              {/* Sample DocSite preview chrome */}
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden mt-5">
                <div className="bg-slate-700 px-4 py-2.5 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                  </div>
                  <span className="text-slate-300 ml-2 text-[11px] font-mono">docsite.care/dr/your-name</span>
                </div>
                <div className="p-5 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-100 to-emerald-100 flex items-center justify-center">
                      <Stethoscope size={20} className="text-sky-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>Dr. Your Name</p>
                      <p className="text-xs text-sky-600">{data.specialty_name} · X yrs experience</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {['Procedures', 'Locations', 'Bookings'].map((t) => (
                      <div key={t} className="rounded-lg border border-slate-100 bg-slate-50 p-2 text-center">
                        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{t}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-400 leading-relaxed">
                    A live preview structure. Your slug, your photo, your case studies, your direct WhatsApp — built and indexed by Google in ~10 minutes.
                  </p>
                  <Link to="/dr/dr-rajesh-sharma" className="text-xs text-sky-600 hover:text-sky-700 font-medium inline-flex items-center gap-1">
                    See a real DocSite practice surface <ArrowRight size={12} />
                  </Link>
                </div>
              </div>

              {/* Quick value bullets — fills right rail with substance */}
              <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-sky-50/60 via-white to-emerald-50/40 p-6 mt-5">
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-sky-700 mb-3">What ships when you join</p>
                <ul className="space-y-3 text-sm text-slate-700">
                  <li className="flex items-start gap-2.5">
                    <Check size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong className="text-slate-900">Google sees you as a doctor, not a webpage.</strong>{' '}
                      Your name, qualifications, and procedures appear as rich results — the kind hospitals usually monopolise.
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Check size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong className="text-slate-900">Every new page reaches Google automatically.</strong>{' '}
                      You publish a procedure or case study — DocSite tells Google and Bing the same hour. Indexing in days, not months.
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Check size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong className="text-slate-900">Patients reach your WhatsApp in one tap.</strong>{' '}
                      No call-centre, no middleman, no commission. The number on the booking screen is yours.
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Check size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong className="text-slate-900">Patient phone numbers and emails stay locked.</strong>{' '}
                      Bank-grade encryption at rest — even if a database leak ever happened, the data is unreadable.{' '}
                      <Link to="/security/patient-data" className="text-emerald-700 hover:text-emerald-800 underline decoration-dotted underline-offset-2" data-testid="patient-data-link-from-specialty">
                        See the 6-layer one-pager you can share with patients →
                      </Link>
                    </span>
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section id="pain-points" className="w-full px-6 md:px-12 lg:px-20 xl:px-28 py-12 md:py-16 scroll-mt-24">
        <h2 className="text-xl md:text-2xl font-medium text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Why most {data.specialty_name.toLowerCase()} practices don't get found online
        </h2>
        <div className="space-y-3" data-testid="for-specialty-pains">
          {data.pain_points.map((p, i) => (
            <div key={i} className="flex items-start gap-3 p-4 rounded-xl border border-rose-100 bg-rose-50/40">
              <AlertTriangle size={16} className="text-rose-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-slate-700 leading-relaxed">{p}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 border-y border-slate-100 py-12 md:py-16">
        <div id="wins" className="w-full px-6 md:px-12 lg:px-20 xl:px-28 scroll-mt-24">
          <h2 className="text-xl md:text-2xl font-medium text-slate-900 mb-6 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Sparkles size={18} className="text-emerald-600" /> What you get on DocSite
          </h2>
          <div className="grid md:grid-cols-3 gap-4" data-testid="for-specialty-wins">
            {data.wins.map((w, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 hover:border-emerald-200 transition-colors">
                <Check size={18} className="text-emerald-600 mb-2" />
                <p className="text-sm text-slate-700 leading-relaxed">{w}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialist-first philosophy — applies to every specialty */}
      <section id="specialist-first" className="w-full px-6 md:px-12 lg:px-20 xl:px-28 py-12 md:py-16 scroll-mt-24" data-testid="for-specialty-philosophy">
        <div className="rounded-3xl border border-sky-100 bg-gradient-to-br from-sky-50/50 via-white to-emerald-50/40 p-8 md:p-12">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-sky-600 mb-3">Our positioning</p>
          <h2 className="text-2xl md:text-3xl font-medium text-slate-900 tracking-tight mb-5 max-w-3xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Why the {data.specialty_name.toLowerCase()} specialist — not a hospital — should be the patient's first call.
          </h2>
          <div className="grid md:grid-cols-3 gap-5 text-sm text-slate-700 leading-relaxed">
            <div className="rounded-2xl bg-white border border-slate-200 p-5">
              <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">The problem</p>
              <p>Today, a Google search for a complex condition surfaces hospital landing pages and aggregator listings before the specialist who would actually evaluate the case. The patient ends up choosing a hospital before they've ever spoken with a doctor.</p>
            </div>
            <div className="rounded-2xl bg-white border border-emerald-200 p-5">
              <p className="text-xs uppercase tracking-wider font-bold text-emerald-700 mb-2">The right order</p>
              <p>Specialist first. The doctor evaluates the patient, weighs the clinical picture against payor mix, family logistics, and recovery setup — then guides the patient to the hospital infrastructure that actually fits this case. Hospitals are partners, not gatekeepers.</p>
            </div>
            <div className="rounded-2xl bg-white border border-sky-200 p-5">
              <p className="text-xs uppercase tracking-wider font-bold text-sky-700 mb-2">What DocSite does</p>
              <p>DocSite is a product platform — not a website builder, not a directory — that returns this conversation to the specialist. Patients reach you directly. Your clinical reasoning, not a marketing budget, becomes the routing layer for which hospital each patient ends up at.</p>
            </div>
          </div>
        </div>
      </section>

      {liveProcedures.length > 0 && (
        <section id="live-procedures" className="w-full px-6 md:px-12 lg:px-20 xl:px-28 py-12 md:py-16 scroll-mt-24" data-testid="for-specialty-live-procedures">
          <div className="flex items-baseline justify-between mb-2 gap-6 flex-wrap">
            <h2 className="text-xl md:text-2xl font-medium text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Published {data.specialty_name.toLowerCase()} procedure pages
            </h2>
            <p className="text-xs text-slate-500">Live examples, authored under truthful-declaration consent.</p>
          </div>
          <p className="text-sm text-slate-500 mb-6 max-w-2xl">
            Each link below is a real per-procedure page written by a {data.specialty_name.toLowerCase()} specialist on DocSite. Open one to see the page structure your DocSite practice surface can have.
          </p>
          <ol className="divide-y divide-slate-100 bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {liveProcedures.map((p, i) => (
              <li key={p.slug + i} data-testid={`live-proc-${p.slug}`}>
                <Link
                  to={`/dr/${p.doctor_slug}/procedures/${p.slug}`}
                  className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors group"
                >
                  <span className="text-[10px] font-mono text-slate-400 w-6 text-right tabular-nums">{String(i + 1).padStart(2, '0')}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 group-hover:text-sky-700 transition-colors">{p.name}</p>
                    {p.type && <p className="text-[11px] text-slate-400 mt-0.5">{p.type}</p>}
                  </div>
                  <ArrowRight size={14} className="text-slate-300 group-hover:text-sky-600 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                </Link>
              </li>
            ))}
          </ol>
          <p className="text-xs text-slate-500 mt-4">
            Your DocSite practice surface gets one page per procedure you offer, just like the examples above — pre-tuned for {data.specialty_name.toLowerCase()} SEO keywords.
          </p>
        </section>
      )}

      <section className="w-full px-6 md:px-12 lg:px-20 xl:px-28 py-12 md:py-16">
        <h2 className="text-xl md:text-2xl font-medium text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Flat pricing. No per-booking deduction.
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          {DEFAULT_PRICING.map((p, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 p-5 bg-white">
              <p className="text-xs uppercase tracking-wide text-sky-600 font-bold">{p.name}</p>
              <p className="text-2xl font-medium text-slate-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{p.price}</p>
              <p className="text-xs text-slate-500 mt-2">{p.subtitle}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link to="/register?as=doctor&from=specialty" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700 transition-colors" data-testid="for-specialty-cta-bottom">
            Build my {data.specialty_name.toLowerCase()} website <ArrowRight size={14} />
          </Link>
        </div>
      </section>
      <section id="faqs" className="bg-slate-50/60 border-t border-slate-100 py-12 md:py-16 scroll-mt-24" data-testid="for-specialty-faqs">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-20">
          <h2 className="text-xl md:text-2xl font-medium text-slate-900 mb-2 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <HelpCircle size={20} className="text-sky-600" /> Frequently asked questions
          </h2>
          <p className="text-xs text-slate-500 mb-6">Generated and maintained by DocSite AI Team. Reviewed quarterly.</p>
          <div className="divide-y divide-slate-200 bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {faqs.map((f, i) => (
              <details key={i} className="group" data-testid={`faq-${i}`}>
                <summary className="flex items-start gap-3 px-5 py-4 cursor-pointer hover:bg-slate-50 transition-colors list-none">
                  <span className="text-[10px] font-mono text-sky-500 mt-1.5 tabular-nums">{String(i + 1).padStart(2, '0')}</span>
                  <span className="flex-1 text-sm font-semibold text-slate-900 group-open:text-sky-700 transition-colors">{f.q}</span>
                  <ArrowRight size={14} className="text-slate-300 mt-1 group-open:rotate-90 transition-transform flex-shrink-0" />
                </summary>
                <div className="px-5 pb-5 pl-12 text-sm text-slate-600 leading-relaxed">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-slate-100 py-12 md:py-16" aria-label="Related specialties and cities" data-testid="for-specialty-internal-links">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28">
          <h2 className="text-base font-bold text-slate-500 uppercase tracking-[0.18em] mb-6">Explore DocSite</h2>
          <div className="grid md:grid-cols-2 gap-10">
            <div>
              <p className="text-sm font-semibold text-slate-900 mb-3">Other specialty platforms</p>
              <ul className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                {relatedSpecialties.map(s => (
                  <li key={s.slug}>
                    <Link to={`/for/${s.slug}`} className="text-sky-700 hover:underline">{s.name}</Link>
                  </li>
                ))}
                <li className="col-span-2 mt-2">
                  <Link to="/for" className="text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">All 30 specialties <ArrowRight size={12} /></Link>
                </li>
              </ul>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900 mb-3">Find {data.specialty_name.toLowerCase()} specialists by city</p>
              <ul className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                {relatedCities.map(c => (
                  <li key={c.slug}>
                    <Link to={`/find/${specialty}/${c.slug}`} className="text-sky-700 hover:underline">
                      {data.specialty_name} in {c.name}
                    </Link>
                  </li>
                ))}
                <li className="col-span-2 mt-2">
                  <Link to={`/find/${specialty}`} className="text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">All cities <ArrowRight size={12} /></Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
